import { useModal } from "@newsela/angelou";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

import { VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE } from "@/assignments/constants/experiments";
import useGetExportables from "@/assignments/useGetExportables";
import { GRAPHIC_ORGANIZER_PARAM } from "@/components/AIComponents/ExportablesModal";
import {
  TEMPLATE_REGISTRY,
  type TemplateDefinition,
} from "@/components/AIComponents/ExportablesModal/templates/registry";
import useHasFeature from "@/features/useHasFeature";
import { isSupportedEnglishContent } from "@/utils/generatedContent";
import { ExportablesContext } from "./useExportablesModalContext";

import type {
  ExportablesModalCommonProps,
  ExportablesModalContextValue,
} from "./ExportablesModalContext.types";

interface ExportablesModalContextProps extends ExportablesModalCommonProps {
  children: React.ReactNode;
  isStudent: boolean;
}

const ExportablesModalContext = ({
  children,
  content,
  contentMetadata = [],
  currentLevel,
  isStudent,
  levelId,
}: ExportablesModalContextProps) => {
  const contentId = content.id;
  const contentType = content.content_type;

  const router = useRouter();
  const { pathname, query } = router;

  const [modalProps, modalSetActive, modalTriggerRef] = useModal(false);
  const [formData, setFormData] = useState<Record<string, Record<string, any>>>(
    {},
  );
  const [selectedExportable, setSelectedExportable] = useState(
    TEMPLATE_REGISTRY.AnticipationGuide,
  );
  const [templatesToAssign, setTemplatesToAssign] = useState<
    TemplateDefinition<any>[]
  >([]);

  const [showAssignmentWarning, setShowAssignmentWarning] = useState(false);

  // TODO: filter list of templates based on student assignments [LESS-4829]
  const templateList: TemplateDefinition<any>[] = isStudent
    ? (
        (query["studentGos"] ??
          "anticipation_guide,compare_contrast,know_wonder_learn") as string
      )
        .split(",")
        .map((go) =>
          (Object.values(TEMPLATE_REGISTRY) as TemplateDefinition<any>[]).find(
            (t) => t.seti_identifier === go,
          ),
        )
    : Object.values(TEMPLATE_REGISTRY);

  const isContentSupported = isSupportedEnglishContent({
    contentType,
    currentLevel,
    contentMetadata,
  });

  const [isInAssignGraphicOrganizersExperiment] = useHasFeature(
    VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE,
  );

  const { data: exportablesMetadata, loading: exportablesLoading } =
    useGetExportables({
      articleId: contentId,
      levelId,
      skip: !isContentSupported,
    }) || {};

  const isLoading = exportablesLoading || exportablesMetadata === null;

  const handleModalClose = () => {
    modalSetActive(false);

    const newQuery = { ...query };
    delete newQuery[GRAPHIC_ORGANIZER_PARAM];

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const handleTemplateSelection = (template?: TemplateDefinition<any>) => {
    const templateToSelect = template || templateList[0];

    if (!templateToSelect) return;

    setSelectedExportable(templateToSelect);

    const newQuery = {
      ...query,
      [GRAPHIC_ORGANIZER_PARAM]: templateToSelect.seti_identifier,
    };

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const initializedTemplates = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedExportable || !exportablesMetadata) return;

    // If the modal is closed, reset initialized templates
    if (!modalProps.isActive) {
      initializedTemplates.current.clear();
      setFormData({});
      return;
    }

    // If we've already initialized this template, don't do it again
    if (initializedTemplates.current.has(selectedExportable.seti_identifier)) {
      return;
    }

    const templateData =
      exportablesMetadata?.[selectedExportable.seti_identifier] || {};

    setFormData((prevFormData) => ({
      ...prevFormData,
      [selectedExportable.seti_identifier]: {
        ...templateData,
        articleShortTitle: currentLevel?.short_title,
      },
    }));

    // Mark this template as initialized
    initializedTemplates.current.add(selectedExportable.seti_identifier);
  }, [
    currentLevel?.short_title,
    selectedExportable?.seti_identifier,
    JSON.stringify(exportablesMetadata),
    modalProps?.isActive,
  ]);

  useEffect(() => {
    const graphicOrganizerParam = query[GRAPHIC_ORGANIZER_PARAM];

    if (graphicOrganizerParam && typeof graphicOrganizerParam === "string") {
      const template = templateList.find(
        (t) => t.seti_identifier === graphicOrganizerParam,
      );

      if (template) {
        setSelectedExportable(template);
        modalSetActive(true);
      }
    }
  }, [query, modalSetActive]);

  const contextValue: ExportablesModalContextValue = {
    content,
    contentMetadata,
    currentLevel,
    formData,
    handleModalClose,
    handleTemplateSelection,
    isContentSupported,
    isInAssignGraphicOrganizersExperiment,
    isLoading,
    levelId,
    modalProps,
    modalSetActive,
    modalTriggerRef,
    selectedExportable,
    setFormData,
    setSelectedExportable,
    setShowAssignmentWarning,
    setTemplatesToAssign,
    showAssignmentWarning,
    templateList,
    templatesToAssign,
  };

  return (
    <ExportablesContext.Provider value={contextValue}>
      {children}
    </ExportablesContext.Provider>
  );
};

export default ExportablesModalContext;
